import memoize from 'lodash.memoize';
import { createSelector } from 'reselect';
import mapById from '../../utility/mapById';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';
import { getAllAnnotationPages, getAllAnnotations, getAllCanvases, getAllContentResources, getAllResources, getAllServices } from '../all';
import mapByIdOrId from '../../utility/mapByIdOrId';
import mapAllResources from '../../utility/mapAllResources';
import mapAllById from '../../utility/mapAllById';
import { isImageService } from '../../constants/services';

var canvas = memoize(function (selector) {
  /**
   * Technical properties
   *
   * - getId
   * - getType
   * - getHeight
   * - getWidth
   * - getDuration
   * - getBehavior
   */
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getHeight = createSelector(selector, technical.getHeight);

  var getWidth = createSelector(selector, technical.getWidth);

  var getDuration = createSelector(selector, technical.getDuration);

  var getBehavior = createSelector(selector, technical.getBehavior);

  /**
   * Descriptive Properties
   *
   * - getLabel
   * - getMetadata
   * - getSummary
   * - getThumbnail
   * - getPosterCanvas
   * - getRequiredStatement
   * - getRights
   * - getNavDate
   */
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getSummary = createSelector(selector, descriptive.getSummary);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);

  var getThumbnail = createSelector(getThumbnailId, getAllContentResources, mapByIdOrId);

  var getPosterCanvasId = createSelector(selector, descriptive.getPosterCanvas);

  var getPosterCanvas = createSelector(getPosterCanvasId, getAllCanvases, mapById);

  var getRequiredStatement = createSelector(selector, descriptive.getRequiredStatement);

  var getRights = createSelector(selector, descriptive.getRights);

  var getNavDate = createSelector(selector, descriptive.getNavDate);

  /**
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getLogo
   * - getHomepage
   * - getRendering
   * - getPartOf
   */
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllContentResources, mapAllById);

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, mapAllById);

  var getLogoIds = createSelector(selector, linking.getLogo);
  var getLogo = createSelector(getLogoIds, getAllContentResources, mapAllById);

  var getHomepageId = createSelector(selector, linking.getHomepage);
  var getHomepage = createSelector(getHomepageId, getAllContentResources, mapById);

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllContentResources, mapAllById);

  var getPartOfId = createSelector(selector, linking.getPartOf);
  var getPartOf = createSelector(getPartOfId, getAllResources, mapAllResources);

  /**
   * Structural Properties
   *
   * - getItems
   * - getStructures
   * - getAnnotations
   */
  var getItemIds = createSelector(selector, structural.getItems);
  var getItems = createSelector(getItemIds, getAllAnnotationPages, mapAllById);

  var getPaintingAnnotationIds = getItemIds;
  var getPaintingAnnotations = getItems;

  var getAnnotationIds = createSelector(selector, structural.getAnnotations);
  var getAnnotations = createSelector(getAnnotationIds, getAllAnnotationPages, mapAllById);

  /**************************************************
   * Algorithms
   *
   * - getImageService
   **************************************************/
  var getImageService = createSelector(getItems, getAllAnnotations, getAllServices, getAllContentResources, function (images, allAnnotations, allServices, allImages) {
    return (
      // The basic path is canvas.annotationPage[x].annotation[x].body[x].service
      images.reduce(function (annotationList, next) {
        (next.items || []).forEach(function (annotationId) {
          allAnnotations[annotationId].body.forEach(function (body) {
            annotationList.push(body);
          });
        });
        return annotationList;
      }, []).reduce(function (result, image) {
        if (result) return result;
        var resource = image.schema === 'contentResource' ? allImages[image.id] : { service: [] };

        return (resource.service || []).reduce(function (innerResult, serviceId) {
          if (innerResult) return innerResult;
          var service = allServices[serviceId];
          if (isImageService(service.profile)) {
            return service;
          }
        }, null);
      }, null)
    );
  });

  return {
    // Technical
    getId: getId,
    getType: getType,
    getHeight: getHeight,
    getWidth: getWidth,
    getDuration: getDuration,
    getBehavior: getBehavior,
    // Descriptive
    getLabel: getLabel,
    getMetadata: getMetadata,
    getSummary: getSummary,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    getPosterCanvasId: getPosterCanvasId,
    getPosterCanvas: getPosterCanvas,
    getRequiredStatement: getRequiredStatement,
    getRights: getRights,
    getNavDate: getNavDate,
    // Linking
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    getServiceIds: getServiceIds,
    getService: getService,
    getLogoIds: getLogoIds,
    getLogo: getLogo,
    getHomepageId: getHomepageId,
    getHomepage: getHomepage,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getPartOfId: getPartOfId,
    getPartOf: getPartOf,
    // Structural
    getItemIds: getItemIds,
    getItems: getItems,
    getPaintingAnnotationIds: getPaintingAnnotationIds,
    getPaintingAnnotations: getPaintingAnnotations,
    getAnnotationIds: getAnnotationIds,
    getAnnotations: getAnnotations,
    // Algorithms
    getImageService: getImageService
  };
});

export default canvas;